/* eslint-disable react/jsx-no-duplicate-props */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { isMobile } from 'react-device-detect';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Alert,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CheckCircleOutline,
  ErrorOutline,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { HTTPError } from 'services/api/entities';
import { LoadingButton, AlertDialog } from 'components';
import type { UserStore } from 'services/store/user/types';
import AccountDeletedModal from 'components/Modals/implementations/AccountDeletedModal/AccountDeletedModal';
import useStyles from './style';
import useValidationSchema from './services/useValidationSchema';

let previousEmail = '';

type PropsType = {
  isUpdateLoading: boolean;
  updateError?: HTTPError;
  updateSucceeded: boolean;
  isDeleteLoading: boolean;
  deleteError?: HTTPError;
  user: UserStore;
  userUpdate: (
    userId: string,
    user: {
      firstName: string;
      lastName: string;
      email: string;
      password?: string;
    }
  ) => void;
  userDelete: (userId: string) => void;
  resetDeleteError: () => void;
};

const Overview = ({
  isUpdateLoading,
  updateError,
  updateSucceeded,
  isDeleteLoading,
  deleteError,
  user,
  userUpdate,
  userDelete,
  resetDeleteError,
}: PropsType) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const [isCheckingEmailAvailability, setIsCheckingEmailAvailability] =
    useState(false);

  previousEmail = user.email;

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isAlertDialogVisible, setIsAlertDialogVisible] = useState(false);
  const [isAccountDeleted, setIsAccountDeleted] = useState(false);

  const shouldCheckEmailAvailability = (emailToCheck: string): boolean => {
    const shouldSendRequest = previousEmail !== emailToCheck;
    previousEmail = emailToCheck;
    return shouldSendRequest;
  };

  const editSchema = useValidationSchema(
    shouldCheckEmailAvailability,
    () => {
      setIsCheckingEmailAvailability(true);
    },
    () => {
      setIsCheckingEmailAvailability(false);
    }
  );

  const { register, handleSubmit, formState, setValue, watch } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(editSchema),
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      password: '',
    },
  });
  const { errors, isValid } = formState;

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const deleteAccount = () => {
    setIsAlertDialogVisible(true);
  };

  const onCloseAlertDialog = () => {
    setIsAlertDialogVisible(false);
  };

  const onSubmit = (
    // @ts-ignore
    data
  ) => {
    // do not send empty password
    if (!data.password) {
      // eslint-disable-next-line no-param-reassign
      delete data.password;
    }
    userUpdate(user.id, data);
  };

  const confirmDeleteAccount = () => {
    userDelete(user.id);
    setIsAccountDeleted(true);
    onCloseAlertDialog();
  };

  return (
    <Container className={classes.formContainer}>
      <Typography
        data-testid="header"
        variant={isMobile ? 'h3' : 'h2'}
        className={classes.header}
      >
        {t('auth:contactInfo')}
      </Typography>
      <Collapse in={!!updateError}>
        <Alert
          variant="outlined"
          severity="error"
          color="error"
          data-testid="errorAlert"
          className={classes.alert}
          icon={<ErrorOutline color="error" />}
        >
          {updateError && updateError.message}
        </Alert>
      </Collapse>
      <Collapse in={!!updateSucceeded}>
        <Alert
          variant="outlined"
          severity="success"
          data-testid="successAlert"
          className={classes.alert}
          icon={<CheckCircleOutline color="primary" />}
        >
          {`${t('auth:updateSuccess')} `}
        </Alert>
      </Collapse>
      <form
        className={classes.form}
        onSubmit={handleSubmit(onSubmit)}
        data-testid="form"
      >
        <TextField
          className={classes.textField}
          inputProps={{
            'data-testid': 'firstNameInput',
          }}
          error={!!errors.firstName}
          helperText={errors.firstName && errors.firstName.message}
          {...register('firstName')}
          label={t('fields:firstName')}
          variant="outlined"
          id="firstName"
          value={watch('firstName')}
          onChange={(event) => {
            setValue('firstName', event.target.value);
          }}
        />
        <TextField
          className={classes.textField}
          inputProps={{
            'data-testid': 'lastNameInput',
          }}
          error={!!errors.lastName}
          helperText={errors.lastName && errors.lastName.message}
          {...register('lastName')}
          label={t('fields:lastName')}
          variant="outlined"
          id="lastName"
          value={watch('lastName')}
          onChange={(event) => {
            setValue('lastName', event.target.value);
          }}
        />
        <TextField
          className={classes.textField}
          {...register('email')}
          error={!!errors.email}
          helperText={errors.email && errors.email.message}
          variant="outlined"
          label={t('fields:email')}
          id="email"
          value={watch('email')}
          onChange={(event) => {
            setValue('email', event.target.value);
          }}
          inputProps={{
            'data-testid': 'emailInput',
          }}
          InputProps={{
            endAdornment: isCheckingEmailAvailability ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
        />
        <TextField
          className={classes.textField}
          {...register('password')}
          inputProps={{
            'data-testid': 'passwordInput',
          }}
          type={isPasswordVisible ? 'text' : 'password'}
          error={!!errors.password}
          helperText={errors.password && errors.password.message}
          label={t('fields:newPassword')}
          variant="outlined"
          id="password"
          onChange={(event) => {
            setValue('password', event.target.value);
          }}
          InputProps={{
            endAdornment:
              watch('password')?.length > 0 ? (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility} edge="end">
                    {isPasswordVisible ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ) : null,
          }}
        />

        <LoadingButton
          type="submit"
          data-testid="saveButton"
          variant="rounded"
          fullWidth
          disabled={!isValid}
          loading={isUpdateLoading}
        >
          {t('auth:save')}
        </LoadingButton>
      </form>
      {/* @ts-ignore */}
      <Button
        data-testid="deleteButton"
        variant="text"
        align="center"
        onClick={deleteAccount}
        sx={{
          mt: theme.spacing(3),
          [theme.breakpoints.down('sm')]: { alignSelf: 'flex-start' },
        }}
      >
        {t('account:delete:buttonText')}
      </Button>
      <AlertDialog
        title={t('account:delete:title')}
        description={t('account:delete:description')}
        closeText={t('general:action:cancel')}
        submitText={t('general:action:ok')}
        state={{
          isOpen: isAlertDialogVisible,
          isLoading: isDeleteLoading,
          error: deleteError,
        }}
        action={{
          onSubmit: () => {
            confirmDeleteAccount();
          },
          onClose: onCloseAlertDialog,
          onExited: resetDeleteError,
        }}
      />
      {isAccountDeleted && <AccountDeletedModal />}
    </Container>
  );
};

export default Overview;
